import { useState } from "react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  Stack,
  Button,
  Text,
  rem,
  ThemeIcon,
  Group,
  Card,
  Loader,
  TextInput,
  Avatar,
  ScrollArea,
  Badge,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconUserPlus,
  IconCheck,
  IconSearch,
  IconUser,
} from "@tabler/icons-react";
// Define search result type since it may have additional fields from the query
interface SearchPlayer {
  id: string;
  username: string;
  description?: string;
}

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: rem(350),
  },
  searchCard: {
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[4]}`,
    padding: theme.spacing.md,
    borderRadius: theme.radius.md,
    marginBottom: theme.spacing.md,
  },
  playerCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    padding: theme.spacing.sm,
    borderRadius: theme.radius.md,
    cursor: "pointer",
    transition: "background-color 200ms ease, border-color 200ms ease",
    "&:hover": {
      backgroundColor: theme.colors.dark[6],
      border: `1px solid ${theme.colors.cyan[7]}`,
    },
  },
  playerCardSelected: {
    backgroundColor: `${theme.colors.cyan[9]}15`,
    border: `1px solid ${theme.colors.cyan[7]}`,
  },
  playerAvatar: {
    border: `2px solid ${theme.colors.dark[4]}`,
  },
  playerName: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  playerEmail: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
  inviteButton: {
    fontWeight: 600,
    marginTop: theme.spacing.lg,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing.xl,
    color: theme.colors.dark[3],
  },
  selectedBadge: {
    position: "absolute",
    top: rem(-8),
    right: rem(-8),
  },
}));

interface TeamInviteModalProps {
  tournamentId: string;
  teamName: string;
  onSuccess?: () => void;
}

const SEARCH_PLAYERS_QUERY = gql`
  query SearchPlayers($search: String!) {
    players(page: { first: 10 }, search: $search) {
      nodes {
        id
        username
        description
      }
    }
  }
`;

const INVITE_MEMBER_MUTATION = gql`
  mutation InviteTournamentTeamMember($playerId: ID!, $tournamentId: ID!) {
    inviteTournamentTeamMember(playerId: $playerId, tournamentId: $tournamentId)
  }
`;

export const TeamInviteModal = ({
  tournamentId,
  teamName,
  onSuccess,
}: TeamInviteModalProps) => {
  const { classes, cx } = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedPlayer, setSelectedPlayer] = useState<SearchPlayer | null>(
    null,
  );
  const [inviting, setInviting] = useState(false);

  const { data, loading } = useQuery<{
    players: { nodes: SearchPlayer[] };
  }>(SEARCH_PLAYERS_QUERY, {
    variables: { search: debouncedSearch },
    skip: debouncedSearch.length < 2,
  });

  const [inviteMember] = useMutation(INVITE_MEMBER_MUTATION);

  const handleInvite = async () => {
    if (!selectedPlayer) return;

    setInviting(true);
    try {
      const result = await inviteMember({
        variables: {
          playerId: selectedPlayer.id,
          tournamentId,
        },
      });

      if (result?.error) {
        throw new Error(result.error.message);
      }

      notifications.show({
        title: "Invitation Sent",
        message: `Invited ${selectedPlayer.username} to ${teamName}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      onSuccess?.();
      modals.closeAll();
    } catch (error) {
      notifications.show({
        title: "Invitation Failed",
        message:
          error instanceof Error ? error.message : "Failed to send invitation",
        color: "rose",
      });
    } finally {
      setInviting(false);
    }
  };

  const players = data?.players?.nodes || [];

  return (
    <Stack className={classes.container}>
      {/* Header */}
      <Group>
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconUserPlus size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600}>Invite Team Member</Text>
          <Text size="xs" c="dimmed">
            Search and invite players to {teamName}
          </Text>
        </div>
      </Group>

      {/* Search Card */}
      <Card className={classes.searchCard}>
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder="Search players by username or email..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          size="md"
        />

        {/* Search Results */}
        <ScrollArea h={200} mt="md">
          {loading ? (
            <Stack align="center" justify="center" h={180}>
              <Loader color="cyan" size="sm" />
              <Text size="sm" c="dimmed">
                Searching...
              </Text>
            </Stack>
          ) : players.length > 0 ? (
            <Stack gap="xs">
              {players.map((player) => (
                <Card
                  key={player.id}
                  className={cx(classes.playerCard, {
                    [classes.playerCardSelected]:
                      selectedPlayer?.id === player.id,
                  })}
                  onClick={() => setSelectedPlayer(player)}
                  style={{ position: "relative" }}
                >
                  {selectedPlayer?.id === player.id && (
                    <Badge
                      className={classes.selectedBadge}
                      color="cyan"
                      variant="filled"
                      size="xs"
                    >
                      Selected
                    </Badge>
                  )}
                  <Group gap="sm">
                    <Avatar
                      size="md"
                      radius="xl"
                      className={classes.playerAvatar}
                      color="cyan"
                    >
                      <IconUser size={20} />
                    </Avatar>
                    <div style={{ flex: 1 }}>
                      <Text className={classes.playerName}>
                        {player.username}
                      </Text>
                      {player.description && (
                        <Text className={classes.playerEmail}>
                          {player.description}
                        </Text>
                      )}
                    </div>
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : debouncedSearch.length >= 2 ? (
            <div className={classes.emptyState}>
              <IconUser size={32} stroke={1.5} />
              <Text mt="xs">No players found</Text>
              <Text size="xs" c="dimmed">
                Try a different search term
              </Text>
            </div>
          ) : (
            <div className={classes.emptyState}>
              <IconSearch size={32} stroke={1.5} />
              <Text mt="xs">Search for players</Text>
              <Text size="xs" c="dimmed">
                Type at least 2 characters to search
              </Text>
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Selected Player Preview */}
      {selectedPlayer && (
        <Card className={classes.searchCard}>
          <Group justify="space-between">
            <Group gap="sm">
              <Avatar
                size="md"
                radius="xl"
                className={classes.playerAvatar}
                color="cyan"
              >
                <IconUser size={20} />
              </Avatar>
              <div>
                <Text fw={600}>{selectedPlayer.username}</Text>
                <Text size="xs" c="dimmed">
                  Ready to invite
                </Text>
              </div>
            </Group>
            <ThemeIcon size="lg" variant="light" color="green" radius="xl">
              <IconCheck size={18} />
            </ThemeIcon>
          </Group>
        </Card>
      )}

      {/* Invite Button */}
      <Button
        className={classes.inviteButton}
        disabled={!selectedPlayer}
        loading={inviting}
        onClick={handleInvite}
        leftSection={<IconUserPlus size={16} />}
        fullWidth
      >
        Send Invitation
      </Button>
    </Stack>
  );
};

// Helper to open the modal
export const openTeamInviteModal = (props: TeamInviteModalProps) => {
  modals.open({
    title: null,
    centered: true,
    size: "md",
    children: <TeamInviteModal {...props} />,
  });
};
